import { HdAuthorityKey, HdDefinitionKey, HdEnergyType, HdEnergyTypeBase } from '../basic-properties/hd-chart-properties.types';

/** Mapsleutels onder `docs/human-design/types/{lang}/`. */
export type HdTypeInfoKey = 'generator' | 'manifesting-generator' | 'manifestor' | 'projector' | 'reflector';

/** Weergavevolgorde van de types (rapport en curriculum: eerst de generators). */
export const HD_TYPE_INFO_KEYS: readonly HdTypeInfoKey[] = [
  'generator', 'manifesting-generator', 'manifestor', 'projector', 'reflector',
];

/** Basistype per typesleutel, voor de strategie/signatuur/niet-zelf-sleutels. */
export const HD_TYPE_INFO_BASE: Readonly<Record<HdTypeInfoKey, HdEnergyTypeBase>> = {
  'generator': 'generator',
  'manifesting-generator': 'generator',
  'manifestor': 'manifestor',
  'projector': 'projector',
  'reflector': 'reflector',
};

/** Delen van een typesectie; ook de bestandsnamen (`{type}/{part}.md`). */
export type HdTypeInfoPart = 'overview' | 'strategy' | 'signature' | 'subtypes' | 'children' | 'reflection';

export const HD_TYPE_INFO_PARTS: readonly HdTypeInfoPart[] = [
  'overview', 'strategy', 'signature', 'subtypes', 'children', 'reflection',
];

/**
 * Onderwerp van de type-familie: elk info-icoon in het Basis-paneel opent zijn
 * eigen, beperkte paneel (Type-rij → type, Strategie-rij → strategie,
 * Signatuur- en Niet-zelf-rij → signatuur en niet-zelf thema).
 */
export type HdTypeInfoTopic = 'type' | 'strategy' | 'signature';

/** Delen van een typesectie die per onderwerp getoond worden. */
export const HD_TYPE_INFO_TOPIC_PARTS: Readonly<Record<HdTypeInfoTopic, readonly HdTypeInfoPart[]>> = {
  type: ['overview', 'subtypes', 'children', 'reflection'],
  strategy: ['strategy'],
  signature: ['signature'],
};

/** Subtypes = type × autoriteit; `mental` staat voor de externe (omgevings)autoriteit. */
export type HdSubtypeKey = 'emotional' | 'sacral' | 'splenic' | 'ego' | 'selfProjected' | 'mental' | 'lunar';

export const HD_TYPE_INFO_SUBTYPES: Readonly<Record<HdTypeInfoKey, readonly HdSubtypeKey[]>> = {
  'generator': ['emotional', 'sacral'],
  'manifesting-generator': ['emotional', 'sacral'],
  'manifestor': ['emotional', 'splenic', 'ego'],
  'projector': ['emotional', 'splenic', 'ego', 'selfProjected', 'mental'],
  'reflector': ['lunar'],
};

const SUBTYPE_BY_AUTHORITY: Readonly<Record<HdAuthorityKey, HdSubtypeKey>> = {
  emotional: 'emotional',
  sacral: 'sacral',
  splenic: 'splenic',
  ego: 'ego',
  selfProjected: 'selfProjected',
  externalEnvironmental: 'mental',
  lunar: 'lunar',
};

export function hdTypeInfoKey(type: HdEnergyType): HdTypeInfoKey {
  if (type.base === 'generator' && type.modifier === 'manifesting') {
    return 'manifesting-generator';
  }
  return type.base;
}

/** Eigen subtype, of `null` wanneer de combinatie mechanisch niet bestaat. */
export function hdOwnSubtypeKey(typeKey: HdTypeInfoKey, authorityKey: HdAuthorityKey): HdSubtypeKey | null {
  const subtype = SUBTYPE_BY_AUTHORITY[authorityKey];
  return HD_TYPE_INFO_SUBTYPES[typeKey].includes(subtype) ? subtype : null;
}

/** Autoriteiten in hiërarchische volgorde (zoals `deriveAuthority` beslist). */
export const HD_AUTHORITY_INFO_ORDER: readonly HdAuthorityKey[] = [
  'emotional', 'sacral', 'splenic', 'ego', 'selfProjected', 'externalEnvironmental', 'lunar',
];

/** Bestandsnaam onder `docs/human-design/authorities/{lang}/`. */
export const HD_AUTHORITY_INFO_FILES: Readonly<Record<HdAuthorityKey, string>> = {
  emotional: 'emotional',
  sacral: 'sacral',
  splenic: 'splenic',
  ego: 'ego',
  selfProjected: 'self-projected',
  externalEnvironmental: 'mental',
  lunar: 'lunar',
};

export const HD_DEFINITION_INFO_ORDER: readonly HdDefinitionKey[] = [
  'single', 'split', 'tripleSplit', 'quadrupleSplit', 'none',
];

/** Bestandsnaam onder `docs/human-design/definitions/{lang}/`. */
export const HD_DEFINITION_INFO_FILES: Readonly<Record<HdDefinitionKey, string>> = {
  single: 'single',
  split: 'split',
  tripleSplit: 'triple-split',
  quadrupleSplit: 'quadruple-split',
  none: 'none',
};

/** Secties van het Basics-paneel, in leesvolgorde; ook de bestandsnamen. */
export const HD_BASICS_INFO_SECTIONS = [
  'what-is-human-design', 'bodygraph', 'not-self', 'strategy-authority', 'passenger-vehicle-driver', 'deconditioning',
  'observation', 'how-to-use',
] as const;

export type HdBasicsInfoSectionKey = typeof HD_BASICS_INFO_SECTIONS[number];

/**
 * Secties van het paneel "De drie dragers van bewustzijn" (monopool- en
 * kristallenlaag), in leesvolgorde; ook de bestandsnamen onder
 * `docs/human-design/cosmology/{lang}/`.
 */
export const HD_COSMOLOGY_INFO_SECTIONS = ['agents', 'imprint', 'frequency', 'cells'] as const;

export type HdCosmologyInfoSectionKey = typeof HD_COSMOLOGY_INFO_SECTIONS[number];
